import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiError, api } from "../../../shared/api/client";
import {
  AppBadge,
  AppButton,
  AppField,
  AppInput,
} from "../../../shared/components/ui";
import { useFieldHeader, useFieldJourney } from "../../../shared/components/FieldShell";
import { FieldWorkspace } from "../../../shared/components/FieldWorkspace";
import { LifecycleStrip } from "../../../shared/components/LifecycleStrip";
import {
  IconCheck,
  IconError,
  IconHourglass,
  IconInfo,
  IconSpinner,
  IconWarning,
} from "../../../shared/components/icons";
import { factsFromCustomer, resolveJourney } from "../../field/journey";
import { recordPayment, searchCustomers } from "../api";
import { DaysEstimateHint } from "../components/DaysEstimateHint";
import { formatTzs, formatTzsFigure, parseTzsToCents } from "../format";
import {
  enqueuePayment,
  getQueuedPayment,
  usePaymentSync,
  type QueuedPaymentRecord,
} from "../offline";
import {
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHOD_REF_LABELS,
  PAYMENT_METHODS_ORDER,
  type CustomerSearchRow,
  type PaymentMethod,
  type RecordPaymentRequest,
} from "../types";

/**
 * S1 — Record payment (handoff TASK-009, s1-record-payment-* boards).
 * Field/cashier, mobile 360px baseline. Customer lookup (name / number /
 * phone) with warning-tone arrears badge; TZS amount (entered in TZS, sent
 * as integer cents); method picker (M-Pesa / USSD / office cash / agent);
 * provider reference; offline queue with the original Idempotency-Key.
 * Success → S2 verification. Duplicates are caught server-side by the
 * reference + signature + idempotency checks — retry is always safe.
 */

const PAY_IDEM_SESSION_KEY = "pg_pay_form_idem_v1";

function readOrMintIdempotencyKey(): string {
  try {
    const existing = sessionStorage.getItem(PAY_IDEM_SESSION_KEY);
    if (existing && existing.length >= 8) return existing;
  } catch {
    /* sessionStorage unavailable */
  }
  return mintIdempotencyKey();
}

function mintIdempotencyKey(): string {
  const key = crypto.randomUUID();
  try {
    sessionStorage.setItem(PAY_IDEM_SESSION_KEY, key);
  } catch {
    /* best-effort */
  }
  return key;
}

/** Map GET /customers/:id into the S1 search-row shape (prefill from deep-link). */
function customerDetailToSearchRow(customer: {
  id: number;
  customer_number: string;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  ward?: string | null;
  active_contract?: {
    id: number;
    contract_number: string;
    arrears?: number;
  } | null;
}): CustomerSearchRow {
  const name = [customer.first_name, customer.last_name].filter(Boolean).join(" ").trim()
    || customer.customer_number;
  const ac = customer.active_contract;
  return {
    id: customer.id,
    customer_number: customer.customer_number,
    name,
    ward: customer.ward ?? null,
    phone: customer.phone ?? "",
    active_contract: ac
      ? {
          id: ac.id,
          contract_number: ac.contract_number,
          plan_name: null,
          arrears_cents: Math.round((ac.arrears ?? 0) * 100),
        }
      : null,
  };
}

export function S1RecordPayment() {
  useFieldHeader("Payment Records Workspace", "Capture, verify, and trace payment outcomes");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillCustomerId = Number(searchParams.get("customer_id") || "") || null;
  const { online, pending, syncing, sync } = usePaymentSync();

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<CustomerSearchRow | null>(null);
  const [amountTzs, setAmountTzs] = useState("");
  const [method, setMethod] = useState<PaymentMethod>("mobile_money");
  const [reference, setReference] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [queuedLocalId, setQueuedLocalId] = useState<string | null>(null);
  // Survives reload in this tab so a retry cannot mint a second key mid-attempt.
  const [idempotencyKey, setIdempotencyKey] = useState(readOrMintIdempotencyKey);
  const [prefillDone, setPrefillDone] = useState(false);

  const backTo = selected
    ? `/customers/${selected.id}`
    : "/field/payments/new";
  useFieldJourney({ flow: "payments", step: 0, backTo });

  // Deep-link: /field/payments/new?customer_id=123 — auto-select, no re-search
  useEffect(() => {
    if (prefillDone || prefillCustomerId == null) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await api.get<{
          customer: {
            id: number;
            customer_number: string;
            first_name?: string | null;
            last_name?: string | null;
            phone?: string | null;
            ward?: string | null;
            active_contract?: {
              id: number;
              contract_number: string;
              arrears?: number;
            } | null;
          };
        }>(`/customers/${prefillCustomerId}`);
        if (cancelled || !d.customer) return;
        setSelected(customerDetailToSearchRow(d.customer));
        setQuery(d.customer.customer_number || String(d.customer.id));
      } catch {
        if (!cancelled) {
          setFormError("Could not load the selected customer — search to continue");
        }
      } finally {
        if (!cancelled) setPrefillDone(true);
      }
    })();
    return () => { cancelled = true; };
  }, [prefillCustomerId, prefillDone]);

  const search = useQuery({
    queryKey: ["payment-customer-search", query.trim()],
    queryFn: () => searchCustomers(query.trim()),
    enabled: selected === null && query.trim().length >= 2,
    retry: 1,
    staleTime: 15_000,
  });

  const mutation = useMutation({
    mutationFn: (body: RecordPaymentRequest) => recordPayment(body, idempotencyKey),
  });

  const amountCents = parseTzsToCents(amountTzs);

  const resetForm = () => {
    setSelected(null);
    setQuery("");
    setAmountTzs("");
    setReference("");
    setFormError(null);
    setQueuedLocalId(null);
    setIdempotencyKey(mintIdempotencyKey());
    mutation.reset();
  };

  const journey = useMemo(
    () =>
      resolveJourney(
        selected
          ? factsFromCustomer({
              id: selected.id,
              active_contract: selected.active_contract
                ? { id: selected.active_contract.id }
                : null,
            })
          : { leadCaptured: true, kycApproved: true },
      ),
    [selected],
  );

  const missingContract = Boolean(selected && !selected.active_contract);

  const validate = (): RecordPaymentRequest | null => {
    setFormError(null);
    if (!selected) {
      setFormError("Find and select the customer first");
      return null;
    }
    if (!selected.active_contract) {
      setFormError("Blocked: no active contract — create a contract before recording payment");
      return null;
    }
    if (amountCents === null) {
      setFormError("Enter a valid amount in TZS");
      return null;
    }
    if (!reference.trim()) {
      setFormError(`Enter the ${PAYMENT_METHOD_REF_LABELS[method].toLowerCase()}`);
      return null;
    }
    return {
      customer_id: selected.id,
      contract_id: selected.active_contract.id,
      amount_cents: amountCents,
      method,
      provider_reference: reference.trim(),
    };
  };

  const submit = () => {
    const body = validate();
    if (!body || !selected) return;
    if (!online) {
      const record: QueuedPaymentRecord = {
        localId: `pay-${crypto.randomUUID()}`,
        payload: body,
        idempotencyKey,
        status: "pending-sync",
        createdAt: new Date().toISOString(),
        customerName: selected.name,
      };
      enqueuePayment(record);
      setQueuedLocalId(record.localId);
      return;
    }
    mutation.mutate(body);
  };

  // ── Queued (offline variant) ───────────────────────────────────────────────
  if (queuedLocalId) {
    const record = getQueuedPayment(queuedLocalId);
    if (record?.status === "synced") {
      // Synced but invalid/unmatched: no Payment row exists (id is null by
      // design) — show the exception reference instead of navigating.
      if (record.paymentId === undefined) {
        return (
          <div className="pg-state">
            <span className="state-icon error">
              <IconError size={40} />
            </span>
            <h2>Sent to reconciliation</h2>
            <p>
              {formatTzs(record.payload.amount_cents)} for {record.customerName} could not be
              matched after sync.
              {record.exceptionNumber
                ? ` Exception ${record.exceptionNumber} was created — the office will resolve it.`
                : " The office will resolve it."}{" "}
              No allocation was run.
            </p>
            <AppButton variant="secondary" type="button" onClick={resetForm}>
              Record another payment
            </AppButton>
          </div>
        );
      }
      return (
        <div className="pg-state">
          <span className="state-icon success">
            <IconCheck size={40} />
          </span>
          <h2>Queued payment synced</h2>
          <p>
            {record.paymentNumber ?? "Payment"} recorded for {record.customerName} —{" "}
            {formatTzs(record.payload.amount_cents)}.
          </p>
          <AppButton type="button" onClick={() => navigate(`/field/payments/${record.paymentId}/verification`)}>
            Continue to verification
          </AppButton>
        </div>
      );
    }
    if (record?.status === "failed") {
      return (
        <div className="pg-state">
          <span className="state-icon error">
            <IconError size={40} />
          </span>
          <h2>Queued payment rejected</h2>
          <p>{record.failReason ?? "The server rejected this payment. Nothing was recorded."}</p>
          <AppButton variant="secondary" type="button" onClick={resetForm}>
            Start a new payment
          </AppButton>
        </div>
      );
    }
    return (
      <div className="pg-state">
        <span className="state-icon warning">
          <IconHourglass size={40} />
        </span>
        <h2>Payment queued</h2>
        <p>
          {record ? `${formatTzs(record.payload.amount_cents)} for ${record.customerName} — ` : ""}
          will sync automatically when you are back online. The reference and idempotency key are
          kept, so it cannot be applied twice.
        </p>
        {online ? (
          <AppButton type="button" onClick={() => void sync()} disabled={syncing}>
            {syncing ? <IconSpinner size={14} /> : null} Sync now
          </AppButton>
        ) : null}
        <AppButton variant="secondary" type="button" onClick={resetForm}>
          Record another payment
        </AppButton>
      </div>
    );
  }

  // ── Success (recorded) ─────────────────────────────────────────────────────
  if (mutation.isSuccess) {
    const payment = mutation.data.payment;
    // F2 (QC TASK-QC-002): invalid/unmatched intake returns id=null and
    // payment_number=null BY DESIGN — no Payment row exists; the REC-YYYY-####
    // exception IS the reference. Render the exception state inline and never
    // navigate to an id-parameterized route.
    if (payment.status === "invalid" || payment.id === null) {
      return (
        <div className="pg-state">
          <span className="state-icon error">
            <IconError size={40} />
          </span>
          <h2>Sent to reconciliation</h2>
          <p>
            {payment.verification?.failure_reason ??
              "The payment could not be matched to a customer."}
            {payment.exception_number
              ? ` Exception ${payment.exception_number} was created — the office will resolve it.`
              : " The office will resolve it."}{" "}
            No allocation was run.
          </p>
          <AppButton variant="secondary" type="button" onClick={resetForm}>
            Record another payment
          </AppButton>
        </div>
      );
    }
    // Cash / agent collection (and live provider intakes) stay PENDING until
    // officer approval or provider webhook — no token yet.
    if (payment.status === "pending") {
      const cashLike = method === "office_cash" || method === "agent_collection";
      return (
        <div className="pg-state">
          <span className="state-icon warning">
            <IconHourglass size={40} />
          </span>
          <h2>{cashLike ? "Awaiting payment approval" : "Awaiting provider confirmation"}</h2>
          <p>
            {payment.payment_number} — {formatTzs(payment.amount_cents)} recorded for{" "}
            {payment.customer_name ?? "customer"}. A token is issued only after{" "}
            {cashLike
              ? "an authorised officer approves the receipt"
              : "the mobile-money provider confirms the payment"}
            .
          </p>
          <AppButton variant="secondary" type="button" onClick={resetForm}>
            Record another payment
          </AppButton>
        </div>
      );
    }
    return (
      <div className="pg-state">
        <span className="state-icon success">
          <IconCheck size={40} />
        </span>
        <h2>Payment recorded</h2>
        <p>
          {payment.payment_number} — {formatTzs(payment.amount_cents)} from{" "}
          {payment.customer_name ?? "customer"}.
          {mutation.data.replayed ? " (already recorded — replay)" : ""}
        </p>
        <AppButton
          type="button"
          onClick={() => navigate(`/field/payments/${payment.id}/verification`)}
        >
          Continue to verification
        </AppButton>
        <AppButton
          variant="secondary"
          type="button"
          onClick={() => navigate("/field/installations")}
        >
          Continue to next
        </AppButton>
        <AppButton variant="secondary" type="button" onClick={resetForm}>
          Record another payment
        </AppButton>
      </div>
    );
  }

  const apiErr = mutation.error instanceof ApiError ? mutation.error : null;
  const busy = mutation.isPending;

  return (
    <FieldWorkspace
      left={
        <>
          <LifecycleStrip journey={journey} title="Customer journey" />
          <div className="pg-summary">
            <div className="row">
              <span className="k">Local steps</span>
              <span className="v">Record → Verify → Allocate → Token → Timeline</span>
            </div>
            <div className="row">
              <span className="k">Connectivity</span>
              <span className="v">{online ? "Online" : "Offline"}</span>
            </div>
            <div className="row">
              <span className="k">Pending sync</span>
              <span className="v">{pending}</span>
            </div>
            <div className="row">
              <span className="k">Idempotency</span>
              <span className="v">Safe retries enabled</span>
            </div>
            <div className="row">
              <span className="k">Next owner</span>
              <span className="v">
                {missingContract ? "Commercial — create contract" : "Field cashier / office"}
              </span>
            </div>
          </div>
          {pending > 0 && online ? (
            <div className="pg-actions" style={{ position: "static", paddingBottom: 0 }}>
              <AppButton variant="secondary" type="button" onClick={() => void sync()} disabled={syncing}>
                {syncing ? <IconSpinner size={14} /> : null} Sync queued payments
              </AppButton>
            </div>
          ) : null}
        </>
      }
      right={
        <>
          {pending > 0 ? (
            <div className="pg-offline-banner" role="status">
              <IconHourglass size={14} /> {pending} payment{pending === 1 ? "" : "s"} queued for sync
              {online ? (
                <button type="button" className="pg-linklike" onClick={() => void sync()} disabled={syncing}>
                  Sync now
                </button>
              ) : null}
            </div>
          ) : null}

          {/* ── Customer lookup ── */}
          <AppField label="Customer">
        {selected ? (
          <div className="pg-candidate">
            <div className="cand-head">
              <span className="cand-name">{selected.name}</span>
              <button
                type="button"
                className="pg-linklike"
                onClick={() => {
                  setSelected(null);
                  setQuery("");
                }}
              >
                Change
              </button>
            </div>
            <div className="cand-evidence">
              {[
                selected.customer_number,
                selected.active_contract?.plan_name ?? selected.active_contract?.contract_number ?? null,
                selected.ward,
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
            {selected.active_contract && selected.active_contract.arrears_cents > 0 ? (
              <AppBadge tone="warning">
                <IconWarning size={12} /> Arrears {formatTzs(selected.active_contract.arrears_cents)}
              </AppBadge>
            ) : null}
            {!selected.active_contract ? (
              <div className="pg-inline-error" role="alert">
                <IconWarning size={14} /> Blocked: no active contract — create a contract
                before recording payment.
                <button
                  type="button"
                  className="pg-linklike"
                  style={{ marginLeft: 8 }}
                  onClick={() => navigate(`/customers/${selected.id}?contract=activate`)}
                >
                  Go to Contract
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <>
            <AppInput
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, customer no. or phone — e.g. CT-2026-0075"
              inputMode="search"
              aria-label="Search customer"
            />
            {query.trim().length >= 2 ? (
              search.isPending ? (
                <div className="pg-idem-note">
                  <IconSpinner size={14} /> Searching customers…
                </div>
              ) : search.isError ? (
                <div className="pg-inline-error" role="alert">
                  <IconError size={14} /> Customer search failed — check your connection and retry.
                </div>
              ) : search.data && search.data.customers.length === 0 ? (
                <div className="pg-inline-error" role="alert">
                  <IconWarning size={14} /> Customer not found — check the number, or register the
                  customer first.
                </div>
              ) : (
                <div className="pg-option-list">
                  {search.data?.customers.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="pg-option"
                      onClick={() => setSelected(c)}
                    >
                      <span>
                        <span className="o-name">{c.name}</span>
                        <span className="pg-option-meta">
                          {c.customer_number}
                          {c.ward ? ` · ${c.ward}` : ""}
                        </span>
                      </span>
                      {c.active_contract && c.active_contract.arrears_cents > 0 ? (
                        <AppBadge tone="warning">
                          <IconWarning size={12} /> {formatTzs(c.active_contract.arrears_cents)}
                        </AppBadge>
                      ) : null}
                    </button>
                  ))}
                </div>
              )
            ) : null}
          </>
        )}
          </AppField>

          {/* ── Amount ── */}
          <AppField label="Amount received (TZS)">
        <AppInput
          value={amountTzs}
          onChange={(e) => setAmountTzs(e.target.value)}
          placeholder="50,000"
          inputMode="numeric"
          aria-label="Amount received in TZS"
        />
        {amountCents !== null ? (
          <span className="pg-amount-preview">TZS {formatTzsFigure(amountCents)}</span>
        ) : null}
        {selected && amountCents !== null && !missingContract ? (
          <DaysEstimateHint
            amountCents={amountCents}
            customerId={selected.id}
            contractId={selected.active_contract?.id}
          />
        ) : null}
          </AppField>

          {/* ── Method picker ── */}
          <AppField label="Payment method">
        <div className="pg-method-grid" role="group" aria-label="Payment method">
          {PAYMENT_METHODS_ORDER.map((m) => (
            <button
              key={m}
              type="button"
              className={`pg-option${method === m ? " selected" : ""}`}
              aria-pressed={method === m}
              onClick={() => setMethod(m)}
            >
              {method === m ? <IconCheck size={14} /> : null}
              {PAYMENT_METHOD_LABELS[m]}
            </button>
          ))}
        </div>
          </AppField>

          {/* ── Reference ── */}
          <AppField label={PAYMENT_METHOD_REF_LABELS[method]}>
        <AppInput
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder={method === "mobile_money" ? "TZK5P9W1QX" : "Receipt / reference no."}
          autoCapitalize="characters"
          aria-label={PAYMENT_METHOD_REF_LABELS[method]}
        />
          </AppField>

          <div className="pg-idem-note">
        <IconInfo size={14} /> Safe to retry — duplicates are caught by the reference + signature
        idempotency check.
          </div>

          {formError ? (
        <div className="pg-inline-error" role="alert">
          <IconWarning size={14} /> {formError}
        </div>
          ) : null}

          {mutation.isError ? (
        <div className="pg-inline-error" role="alert">
          <IconError size={14} /> Payment could not be recorded — {apiErr?.message ?? "network error"}.
          Nothing was applied; retry is safe, your entries are preserved.
        </div>
          ) : null}

          <div className="pg-actions">
            {missingContract ? (
              <AppButton
                type="button"
                onClick={() => selected && navigate(`/customers/${selected.id}?contract=activate`)}
              >
                Go to Contract
              </AppButton>
            ) : (
              <AppButton type="button" onClick={submit} disabled={busy}>
                {busy ? <IconSpinner size={14} /> : null}{" "}
                {online ? "Record payment" : "Queue payment (offline)"}
              </AppButton>
            )}
          </div>
        </>
      }
    />
  );
}
